import {
  REALTIME_SUBSCRIBE_STATES,
  type RealtimeChannel,
  type SupabaseClient,
} from '@supabase/supabase-js';
import { REALTIME_TOKEN_REFRESH_LEAD_SECONDS, realtimeTokenResponseSchema } from '@aegislab/shared';
import { useAuthStore } from '@/shared/model';
import { apiClient } from './client';

/**
 * 실시간 채널 인증 (ADR-0036).
 *
 * 앱 JWT 는 쿠키로만 다니고 Supabase 가 읽지 못한다. 백엔드 `POST realtime/token` 이 앱 JWT 를
 * 확인한 뒤 Supabase 가 검증하는 단명 토큰을 주고, 여기서 그 토큰을 `realtime.setAuth` 로 붙인다.
 * realtime-js 는 채널에 들어갈 때 그 시점의 토큰을 실어 보내므로(`accessTokenValue`), 비공개 채널은
 * 토큰이 붙은 **뒤에** 구독해야 한다 — 훅은 `subscribeWhenAuthed` 를 쓴다.
 *
 * 토큰은 만료 5분 전에 갈아 끼운다. `setAuth(새 토큰)` 은 이미 붙은 채널에도 전달된다.
 */
let client: SupabaseClient | null = null;
let token: string | null = null;
let inflight: Promise<string> | null = null;
let refreshTimer: ReturnType<typeof setTimeout> | null = null;

let authWatched = false;

/** getSupabase 가 클라이언트를 만든 직후 한 번 부른다. */
export function bindRealtimeAuth(supabase: SupabaseClient): void {
  client = supabase;
  // 로그아웃·세션 만료로 사용자가 비면 토큰을 버린다. 다음 로그인의 첫 구독이 새 토큰을 받는다.
  // 모듈 로드 시점이 아니라 여기서 거는 이유: 많은 테스트가 shared/model 을 통째로 mock 해
  // subscribe 가 없는 객체를 준다(authStore 가 shared/api 를 가져오는 방향은 client.ts 선례와 같다).
  const store = useAuthStore as { subscribe?: typeof useAuthStore.subscribe };
  if (!authWatched && typeof store.subscribe === 'function') {
    authWatched = true;
    store.subscribe((state, previous) => {
      if (previous.user && !state.user) resetRealtimeAuth();
    });
  }
}

function clearRefreshTimer(): void {
  if (refreshTimer) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

async function fetchAndApply(): Promise<string> {
  const response = await apiClient.post('realtime/token');
  const parsed = realtimeTokenResponseSchema.parse(response.data);
  await client?.realtime.setAuth(parsed.token);
  token = parsed.token;
  clearRefreshTimer();
  const leadMs = Math.max(parsed.expiresIn - REALTIME_TOKEN_REFRESH_LEAD_SECONDS, 1) * 1000;
  refreshTimer = setTimeout(() => {
    refreshTimer = null;
    void ensureRealtimeAuth({ force: true }).catch(() => {
      // 갱신 실패는 다음 구독·재연결이 다시 시도한다. 만료되면 realtime-js 가 채널을 닫고
      // 훅의 재시도 경로가 subscribeWhenAuthed 로 돌아온다.
    });
  }, leadMs);
  return parsed.token;
}

/** 토큰이 붙어 있으면 그대로, 없으면 받아서 붙인다. 동시 호출은 한 요청을 같이 기다린다. */
export function ensureRealtimeAuth(options: { force?: boolean } = {}): Promise<string> {
  if (inflight) return inflight;
  if (token && !options.force) return Promise.resolve(token);
  inflight = fetchAndApply().finally(() => {
    inflight = null;
  });
  return inflight;
}

/** 로그아웃·계정 전환 — 다음 구독이 새 토큰을 받게 한다. */
export function resetRealtimeAuth(): void {
  token = null;
  inflight = null;
  clearRefreshTimer();
}

type SubscribeCallback = Parameters<RealtimeChannel['subscribe']>[0];
type StatusListener = NonNullable<SubscribeCallback>;
type Subscription = {
  listeners: Set<StatusListener>;
  status?: Parameters<StatusListener>;
};
const subscriptions = new WeakMap<RealtimeChannel, Subscription>();

/**
 * 토큰이 붙은 뒤에 구독한다. 토큰을 못 받으면 구독하지 않고 CHANNEL_ERROR 로 알려 훅의
 * 재시도(backoff)가 이어받게 한다.
 */
export function subscribeWhenAuthed(
  channel: RealtimeChannel,
  callback?: SubscribeCallback,
  signal?: AbortSignal
): void {
  if (signal?.aborted) return;
  const existing = subscriptions.get(channel);
  if (!existing && channel.joinedOnce) return;

  const subscription = existing ?? { listeners: new Set<StatusListener>() };
  const listener: StatusListener = (status, error) => {
    if (!signal?.aborted) callback?.(status, error);
  };
  subscription.listeners.add(listener);
  signal?.addEventListener(
    'abort',
    () => {
      subscription.listeners.delete(listener);
      if (
        subscription.listeners.size === 0 &&
        !channel.joinedOnce &&
        subscriptions.get(channel) === subscription
      ) {
        subscriptions.delete(channel);
      }
    },
    { once: true }
  );
  if (existing) {
    const status = existing.status;
    if (status) queueMicrotask(() => listener(...status));
    return;
  }
  subscriptions.set(channel, subscription);

  const notify: StatusListener = (status, error) => {
    subscription.status = [status, error];
    subscription.listeners.forEach((onStatus) => {
      onStatus(status, error);
    });
  };
  void ensureRealtimeAuth()
    .then(() => {
      if (subscription.listeners.size === 0 || channel.joinedOnce) return;
      channel.subscribe(notify);
    })
    .catch((error: unknown) => {
      if (subscriptions.get(channel) === subscription) subscriptions.delete(channel);
      notify(
        REALTIME_SUBSCRIBE_STATES.CHANNEL_ERROR,
        error instanceof Error ? error : new Error(String(error))
      );
    });
}
